<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useYardInTransitFeature } from '@satellite/composables/yard/useYardInTransitFeature';
import { useYardTaskPresenter } from '@satellite/composables/useYardTaskPresenter';
import { getYardTaskBlockMessageSegments } from '@satellite/helpers/yardTaskBlockReasons';

import type { Maybe, TaskViewData } from '@nova/core';

const { task } = defineProps<{
  task: Maybe<TaskViewData>;
}>();

const { t } = useI18n();
const { isYardInTransitVisible } = useYardInTransitFeature();

const { isBlocked, isTaskCreated, isTaskInProgress, destinationSpotLabel } = useYardTaskPresenter(
  computed(() => task),
  { i18n: t }
);

/**
 * Created and in-progress are the states the backend blocks, so they are the states that explain
 * themselves. A completed or cancelled task can still carry the tag, and there the block is history.
 */
const isTaskOpen = computed(() => isTaskCreated.value || isTaskInProgress.value);

/**
 * `isBlocked` (the `blocked` tag) never reflects two of the five conditions — another move already
 * in transit toward this destination, or this task's own asset already in transit under a
 * different move — since nothing reconciles the tag for those. `blockDetail` is resolved live and
 * catches them too, so either signal is enough to show the banner.
 */
const hasLiveBlockReason = computed(() => Boolean(task?.blockDetail?.reason));

/**
 * The generic fallback message names the destination spot, so it needs one to say anything
 * meaningful — but the reason-specific copy never does (not even destination-in-transit, which
 * names the *inbound asset*, not the spot), and asset-elsewhere has no destination at all on a "to
 * any spot" task. Only require the label when there's no live reason to fall back on.
 */
const isVisible = computed(
  () =>
    isYardInTransitVisible.value &&
    (isBlocked.value || hasLiveBlockReason.value) &&
    isTaskOpen.value &&
    (hasLiveBlockReason.value || Boolean(destinationSpotLabel.value))
);

/**
 * The reason-specific copy (OD-4117) takes over whenever the backend resolved one. The generic
 * "blocked until open" string is the fallback for the cases the resolver doesn't cover (flag off,
 * a reconcile that hasn't caught up yet); it has nothing to bold.
 */
const messageSegments = computed(
  () =>
    getYardTaskBlockMessageSegments(task?.blockDetail, t) ?? [
      {
        text: t('core.yard.messages.blockedUntilSpotIsOpen', { spot: destinationSpotLabel.value }),
        bold: false
      }
    ]
);
</script>

<template>
  <div
    v-if="isVisible"
    class="d-flex flex-row ga-1 align-center justify-center w-100 pa-2 rounded bg--color-neutral-20"
    data-testid="yard-task-blocked-banner">
    <v-icon icon="mdi-lock-outline" size="16" />
    <span class="typography-caption">
      <template v-for="(segment, index) in messageSegments" :key="index">
        <strong v-if="segment.bold">{{ segment.text }}</strong>
        <template v-else>{{ segment.text }}</template>
      </template>
    </span>
  </div>
</template>
